import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Toolbar,
  Divider
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Image as ImageIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';

interface EditableContentProps {
  content: string;
  pageName: string;
  contentKey: string;
  type: 'text' | 'image';
  imageUrl?: string;
  sx?: any;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  onUpdate?: (newContent: string) => void;
}

const SimpleEditor: React.FC<{
  content: string;
  onChange: (content: string) => void;
}> = ({ content, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Toolbar variant="dense" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => execCommand('bold')}>
          <FormatBold />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('italic')}>
          <FormatItalic />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('underline')}>
          <FormatUnderlined />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton size="small" onClick={() => execCommand('insertUnorderedList')}>
          <FormatListBulleted />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('insertOrderedList')}>
          <FormatListNumbered />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton size="small" onClick={() => execCommand('justifyLeft')}>
          <FormatAlignLeft />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('justifyCenter')}>
          <FormatAlignCenter />
        </IconButton>
        <IconButton size="small" onClick={() => execCommand('justifyRight')}>
          <FormatAlignRight />
        </IconButton>
      </Toolbar>
      <Box
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        sx={{
          p: 2,
          minHeight: '200px',
          '&:focus': {
            outline: 'none',
            bgcolor: 'action.hover'
          }
        }}
      />
    </Box>
  );
};

const EditableContent: React.FC<EditableContentProps> = ({
  content,
  pageName,
  contentKey,
  type,
  imageUrl,
  sx,
  onEditStart,
  onEditEnd,
  onUpdate
}) => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(content);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleEditClick = () => {
    setIsEditing(true);
    onEditStart?.();
  };

  const handleClose = () => {
    setIsEditing(false);
    onEditEnd?.();
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (type === 'image' && selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('contentKey', contentKey);

        const response = await fetch(`${config.api.baseUrl}/cms/pages/${pageName}/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Kunde inte spara bilden');
        }
      } else {
        console.log('Sparar innehåll:', {
          pageName,
          contentKey,
          editedContent
        });

        const response = await fetch(`${config.api.baseUrl}/cms/pages/${pageName}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            [contentKey]: editedContent
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server svarade med fel:', errorData);
          throw new Error(errorData.error || 'Kunde inte spara innehållet');
        }

        const responseData = await response.json();
        console.log('Server svarade:', responseData);
        
        // Uppdatera innehållet lokalt och meddela parent
        onUpdate?.(editedContent);
      }

      handleClose();
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Ett fel uppstod när innehållet skulle sparas');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  if (!isAuthenticated || !isAdmin) {
    return type === 'image' ? (
      <Box 
        component="img" 
        src={imageUrl || content} 
        alt="" 
        sx={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          display: (!imageUrl && !content) ? 'none' : 'block'
        }} 
      />
    ) : (
      <Typography sx={sx} dangerouslySetInnerHTML={{ __html: content }} />
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {type === 'image' ? (
        <>
          {imageUrl && (
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                ...sx
              }}
            />
          )}
          <Box
            className="image-edit-icon"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 60,
              height: 60,
              bgcolor: 'rgba(255,255,255,0.8)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 999,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.95)',
                transform: 'translate(-50%, -50%) scale(1.1)'
              }
            }}
          >
            <label style={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              cursor: 'pointer' 
            }}>
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
              <ImageIcon sx={{ width: 32, height: 32 }} />
            </label>
          </Box>
        </>
      ) : (
        <>
          <Typography sx={sx} dangerouslySetInnerHTML={{ __html: content }} />
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            bgcolor: 'rgba(255,255,255,0.8)', 
            borderRadius: 1 
          }}>
            <IconButton onClick={handleEditClick} size="small">
              <EditIcon />
            </IconButton>
          </Box>
        </>
      )}

      <Dialog
        open={isEditing}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Redigera innehåll</DialogTitle>
        <DialogContent>
          <SimpleEditor content={editedContent} onChange={setEditedContent} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Avbryt</Button>
          <Button onClick={handleSave} variant="contained">Spara</Button>
        </DialogActions>
      </Dialog>

      {selectedFile && type === 'image' && (
        <Dialog
          open={!!selectedFile}
          onClose={() => {
            setSelectedFile(null);
            handleClose();
          }}
        >
          <DialogTitle>Förhandsgranska bild</DialogTitle>
          <DialogContent>
            <Box
              component="img"
              src={URL.createObjectURL(selectedFile)}
              alt="Förhandsgranskning"
              sx={{ maxWidth: '100%', maxHeight: '500px' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedFile(null)}>Avbryt</Button>
            <Button onClick={handleSave} variant="contained">Spara</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default EditableContent; 